import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { staffApi } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Search } from 'lucide-react';

export default function Customers() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = searchParams.get('filter') || '';
  const [q, setQ] = useState('');
  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  useEffect(() => {
    const t = setTimeout(() => {
      const params = { q, page };
      if (filter) params.filter = filter;
      staffApi.get('/customers', { params }).then(({ data }) => {
        setCustomers(data.customers);
        setTotal(data.total);
      });
    }, 250);
    return () => clearTimeout(t);
  }, [q, page, filter]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="font-heading text-2xl font-700 text-charcoal">Customers</h1>
        <span className="text-sm text-muted-ink" data-testid="customers-total">{total} total</span>
      </div>

      {filter === 'new' && (
        <div className="flex items-center gap-2 text-sm text-muted-ink">
          <span>
            Showing <strong className="text-charcoal">new customers this week</strong>
          </span>
          <button
            type="button"
            data-testid="customers-filter-clear"
            onClick={() => setSearchParams({})}
            className="text-saffron underline"
          >
            Clear
          </button>
        </div>
      )}

      <div className="relative max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-ink" />
        <Input
          data-testid="customers-search-input"
          placeholder="Search by name, phone, email or customer ID…"
          value={q}
          onChange={(e) => {
            setPage(1);
            setQ(e.target.value);
          }}
          className="pl-9"
        />
      </div>

      <div className="bg-white rounded-xl border border-border/40 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-bone/60">
              <TableHead>Customer ID</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Subscription</TableHead>
              <TableHead>Remaining</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {customers.map((c) => (
              <TableRow
                key={c.id}
                data-testid="customer-row"
                className="cursor-pointer hover:bg-bone/50 transition-colors duration-150"
              >
                <TableCell>
                  <Link to={`/admin/customers/${c.id}`} className="block text-charcoal font-medium" data-testid="customer-row-link">
                    {c.customerCode}
                  </Link>
                </TableCell>
                <TableCell><Link to={`/admin/customers/${c.id}`} className="block">{c.name}</Link></TableCell>
                <TableCell><Link to={`/admin/customers/${c.id}`} className="block">{c.phone}</Link></TableCell>
                <TableCell><Link to={`/admin/customers/${c.id}`} className="block">{c.email}</Link></TableCell>
                <TableCell><Link to={`/admin/customers/${c.id}`} className="block">{c.latestSubscription?.plan?.name || '—'}</Link></TableCell>
                <TableCell>
                  <Link to={`/admin/customers/${c.id}`} className="block">
                    {c.latestSubscription?.status === 'ACTIVE' ? c.latestSubscription.remainingCredits : '—'}
                  </Link>
                </TableCell>
                <TableCell>
                  <Link to={`/admin/customers/${c.id}`} className="block">{c.status}</Link>
                </TableCell>
              </TableRow>
            ))}
            {customers.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-ink py-8">
                  No customers found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {total > 20 && (
        <div className="flex justify-end gap-2">
          <button
            data-testid="customers-prev-page"
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
            className="text-sm px-3 py-1.5 rounded-full border border-border/40 disabled:opacity-40"
          >
            Previous
          </button>
          <button
            data-testid="customers-next-page"
            disabled={page * 20 >= total}
            onClick={() => setPage((p) => p + 1)}
            className="text-sm px-3 py-1.5 rounded-full border border-border/40 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
